<template>
    <v-card>
        <v-card-title class="d-flex flex-column align-start text-subtitle-1 text-sm-h6">
            <span>Rechnung Nr. {{ editedItem.nummer }} wurde bezahlt</span>
            <div v-if="editedItem.selected_customer" class="d-flex align-center">
                <div v-if="editedItem.selected_customer.type === 'client'">
                    <v-icon small left>mdi-account</v-icon>
                    <span class="text-body-2">{{ editedItem.selected_customer.name }}</span>
                    <v-chip v-for="merkmal in editedItem.selected_customer.merkmale" :key="'chip-' + merkmal.id" class="ml-2 font-weight-regular" small>{{ merkmal.merkmal }}</v-chip>
                </div>
                <div v-else-if="editedItem.selected_customer.type === 'institution'">
                    <v-icon small left>mdi-account</v-icon>
                    <span class="text-body-2">{{ editedItem.selected_customer.name }}</span>
                    <v-chip class="ml-2 font-weight-regular" small>Institution</v-chip>
                </div>
            </div>
        </v-card-title>

        <v-card-text>
            <!-- Payment Method Selection (Expandable) -->
            <div class="mb-4">
                <v-expansion-panels v-model="paymentMethodPanel" class="flat-expansion-panels" flat>
                    <v-expansion-panel>
                        <v-expansion-panel-header hide-actions>
                            <div class="d-flex align-center justify-space-between">
                                <div class="d-flex align-center">
                                    <span class="text-body-2 mr-2">Zahlungsmethode:</span>
                                    <v-chip :color="$store.state.theme.primary" dark small>
                                        <v-icon left small>{{ currentPaymentMethodIcon }}</v-icon>
                                        {{ currentPaymentMethodLabel }}
                                    </v-chip>
                                </div>
                                <v-icon :color="$store.state.theme.primary">
                                    {{ paymentMethodPanel === 0 ? 'mdi-chevron-up' : 'mdi-pencil' }}
                                </v-icon>
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-tabs v-model="selectedPaymentMethod" class="mt-3 payment-method-tabs" @change="onPaymentMethodChange" grow hide-slider active-class="payment-tab-active">
                                <v-tab>              
                                    <v-icon left>mdi-bank-transfer</v-icon>              
                                    Überweisung
                                </v-tab>
                                <v-tab>                            
                                    <v-icon left>mdi-cash</v-icon>
                                    Bar
                                </v-tab>
                                <v-tab>
                                    <v-icon left>mdi-credit-card</v-icon>
                                    Bankomat
                                </v-tab>
                            </v-tabs>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </div>

            <!-- Payment Date Section -->
            <v-form ref="form" v-model="formValid">
                <p class="text-subtitle-1 font-weight-medium mb-3">
                    Bezahldatum
                </p>
                <v-text-field 
                    dense 
                    outlined 
                    v-model="paidDateText"
                    label="Bezahlt am" 
                    prepend-inner-icon="mdi-calendar"
                    :rules="dateRule"
                    @input="updateDatePicker"
                    class="font-weight-medium mb-3"
                />
            </v-form>

            <v-date-picker 
                full-width 
                first-day-of-week="1" 
                v-model="editedItem.bezahlt" 
                :color="$store.state.theme.green"
                no-title 
                elevation="1"
                @input="updateTextField" 
                class="py-3"
            />

            <v-row class="d-flex justify-center align-center my-1">
                <span class="text-caption-1">
                    <v-icon left small> mdi-cursor-default-click-outline </v-icon>
                    Wähle das Datum aus, an dem die Rechnung bezahlt wurde.
                </span>
            </v-row>

        </v-card-text>

        <v-card-actions class="px-6 pb-5">
            <v-spacer></v-spacer>
            <v-btn :color="$store.state.theme.primary" text @click="cancel">
                Abbrechen
            </v-btn>
            <v-btn :color="$store.state.theme.green" outlined @click="save" :loading="updating_invoice" :disabled="!editedItem.selected_customer || !formValid">
                Speichern
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import dayjs from 'dayjs';
import connector from '../helpers/supabase-connector.js';

export default {
    props: ['session', 'editedItem', 'editedIndex'],
    emits: ['close', 'initialize', 'showError', 'showInfo'],

    data() {
        return {
            updating_invoice: false,
            paidDateText: '',
            formValid: true,
            selectedPaymentMethod: 0, // 0=Transfer, 1=Cash, 2=Card
            paymentMethodPanel: null, // null = collapsed, 0 = expanded

            dateRule: [
                v => !!v || 'Zahlungsdatum wird benötigt.',
                v => /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[012])\.(\d{4})$/.test(v) || 'Datum im Format TT.MM.JJJJ angeben.',
            ],
        }
    },

    computed: {
        computedPaidFormatted() {
            return this.formatDate(this.editedItem.bezahlt)
        },

        currentPaymentMethodIcon() {
            if (this.editedItem.bankomat) return 'mdi-credit-card';
            if (this.editedItem.bar) return 'mdi-cash';
            return 'mdi-bank-transfer';
        },

        currentPaymentMethodLabel() {
            if (this.editedItem.bankomat) return 'Bankomat';
            if (this.editedItem.bar) return 'Bar';
            return 'Überweisung';
        },

    },

    watch: {
        'editedItem.bezahlt': {
            immediate: true,
            handler(newVal) {
                this.paidDateText = this.formatDate(newVal);
            }
        }
    },

    mounted() {
        this.initializePaymentMethod();
    },

    methods: {
        resetDialog() {
            // Reset expansion panel to collapsed state
            this.paymentMethodPanel = null;
            // Reset payment method selection to match current invoice data
            this.initializePaymentMethod();
        },

        cancel() {
            this.resetDialog();
            this.$emit('close');
        },
        initializePaymentMethod() {
            // Set selected payment method based on existing invoice data
            if (this.editedItem.bankomat) {
                this.selectedPaymentMethod = 2; // Bankomat
            } else if (this.editedItem.bar) {
                this.selectedPaymentMethod = 1; // Bar
            } else {
                this.selectedPaymentMethod = 0; // Überweisung
            }
        },

        onPaymentMethodChange(tabIndex) {
            if (tabIndex === 1) { // Bar (Cash)
                this.editedItem.bar = true;
                this.editedItem.bankomat = false;
            } else if (tabIndex === 2) { // Bankomat (Card)
                this.editedItem.bar = true;
                this.editedItem.bankomat = true;
            } else if (tabIndex === 0) { // Überweisung (Transfer)
                this.editedItem.bar = false;
                this.editedItem.bankomat = false;
            }
        },
        
        formatDate(date) {
            if (!date) return null;
            return dayjs(date).format('DD.MM.YYYY');
        },

        updateTextField(newDate) {
            this.paidDateText = this.formatDate(newDate);
        },

        updateDatePicker() {
            const parsedDate = dayjs(this.paidDateText, 'DD.MM.YYYY', true);
            if (parsedDate.isValid()) {
                this.editedItem.bezahlt = parsedDate.format('YYYY-MM-DD');
            }
        },

        async save() {
            this.updating_invoice = true;

            let to_update = {
                'bezahlt': this.editedItem.bezahlt,
                'bar': this.editedItem.bar || false,
                'bankomat': this.editedItem.bankomat || false
            };

            let updated = await connector.update(this, 'rechnungen', to_update, this.editedIndex);

            if (!updated) {
                // error has already been displayed
                this.updating_invoice = false;
                return;
            }

            this.$emit('initialize');
            this.resetDialog();
            this.$emit('close');
            this.updating_invoice = false;
            this.$emit('showInfo', {
                message: 'Die Rechnung wurde erfolgreich als bezahlt markiert.',
                timeout: 5000
            });
        },
    }
}
</script>

<style scoped>
.flat-expansion-panels ::v-deep .v-expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-bottom: 8px;
}
.flat-expansion-panels ::v-deep .v-expansion-panel:last-child {
  margin-bottom: 0;
}
.payment-method-tabs ::v-deep .v-tabs-bar {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.payment-method-tabs ::v-deep .v-tab:not(:last-child) {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}
.payment-method-tabs ::v-deep .payment-tab-active {
  background-color: hsl(210, 15%, 90%);
  color: hsl(210, 15%, 20%);
}
.payment-method-tabs ::v-deep .v-tab:hover:not(.payment-tab-active) {
  background-color: hsl(210, 10%, 95%);
}
</style>
